import { Children, useEffect, useRef, useState, type ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useLocale } from './i18n';

/**
 * Horizontal slider that follows the mouse or finger 1:1. Dragging past a fifth of a slide (or a
 * quick flick) moves to the neighbouring slide; otherwise it springs back. Arrow keys work while
 * focus is inside. Right-to-left pages run the track mirrored: the next slide comes from the left. The parent owns `active`, so other controls (bars, logo tiles…) can drive it.
 *
 * `gutter` keeps that much room around the slides unclipped (for shadows/glows) while the
 * neighbouring slides stay hidden behind `gap`, which must be larger than the gutter. */
export function SwipeTrack({
  active,
  onChange,
  children,
  gap = 160,
  gutter = 120,
  onDraggingChange,
  className,
}: {
  active: number;
  onChange: (index: number) => void;
  children: ReactNode;
  gap?: number;
  gutter?: number;
  onDraggingChange?: (dragging: boolean) => void;
  className?: string;
}) {
  const slides = Children.toArray(children);
  const count = slides.length;
  const [width, setWidth] = useState(0);
  const [dragging, setDragging] = useState(false);
  const viewport = useRef<HTMLDivElement>(null);
  // A drag that ends over a link or button must not also click it.
  const justDragged = useRef(false);
  const reduce = useReducedMotion();
  const { t, rtl } = useLocale();
  // +1 when the next slide sits to the right (LTR), -1 when it sits to the left (RTL).
  const dir = rtl ? -1 : 1;
  const step = width + gap;

  useEffect(() => {
    const el = viewport.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => setWidth(entries[0]?.contentRect.width ?? 0));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const setDrag = (v: boolean) => {
    setDragging(v);
    onDraggingChange?.(v);
  };
  const go = (i: number) => onChange(Math.min(Math.max(i, 0), count - 1));

  return (
    // The gutter is only room for glows: it must not catch clicks meant for content around it.
    <div
      className="pointer-events-none overflow-hidden"
      style={{ margin: -gutter, padding: gutter }}
    >
      <div
        ref={viewport}
        className={cn('pointer-events-auto', className)}
        onKeyDown={(e) => {
          const forward = rtl ? 'ArrowLeft' : 'ArrowRight';
          const back = rtl ? 'ArrowRight' : 'ArrowLeft';
          if (e.key === forward) onChange((active + 1) % count);
          else if (e.key === back) onChange((active - 1 + count) % count);
        }}
      >
        <motion.div
          className={cn(
            'flex items-stretch select-none',
            count > 1 && (dragging ? 'cursor-grabbing' : 'cursor-grab'),
          )}
          style={{ gap }}
          animate={{ x: -dir * active * step }}
          transition={
            reduce ? { duration: 0 } : { type: 'spring', stiffness: 260, damping: 34, mass: 0.9 }
          }
          drag={count > 1 && width > 0 ? 'x' : false}
          dragConstraints={
            rtl ? { left: 0, right: (count - 1) * step } : { left: -(count - 1) * step, right: 0 }
          }
          dragElastic={0.18}
          dragMomentum={false}
          onDragStart={() => {
            justDragged.current = true;
            setDrag(true);
          }}
          onDragEnd={(_, info) => {
            setDrag(false);
            setTimeout(() => (justDragged.current = false), 0);
            const threshold = width / 5;
            const offset = info.offset.x * dir;
            const velocity = info.velocity.x * dir;
            if (offset < -threshold || velocity < -500) go(active + 1);
            else if (offset > threshold || velocity > 500) go(active - 1);
            // otherwise `animate` is unchanged and the track springs back
          }}
          onClickCapture={(e) => {
            if (justDragged.current) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
        >
          {slides.map((slide, i) => (
            <div
              key={i}
              role="group"
              aria-roledescription="slide"
              aria-label={t.slideOf(i + 1, count)}
              aria-hidden={i !== active}
              inert={i !== active}
              className="flex w-full shrink-0 [&_img]:pointer-events-none"
            >
              {slide}
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}
